import { Button, Modal } from 'antd'
import React from 'react'
import { MODAL_BREDD, MODAL_GRIND, MODAL_KNAPP, MODAL_KNAPP_FARLIG, modalTitel } from '../modalStil'

/**
 * RaderaBesokModal – bekräftelse innan ett pausat besök tas bort.
 *
 * Knappen i utkastrutan verkställde tidigare direkt. Att raden bara försvann var den
 * enda återkopplingen, och det som försvann var ett halvt samtal som inte går att
 * återskapa – brukaren är inte kvar i rummet.
 *
 * Modalen säger också vad som INTE händer. Utan den raden kan "radera besöket" läsas
 * som att hela brukarens historik ryker, och då vågar ingen trycka.
 *
 * ORDVALET. Funktionen har två ord för att göra sig av med ett besök, och skillnaden
 * är verklig: AVBRYT stoppar ett pågående besök som aldrig sparats – det finns
 * ingenting att ta bort. RADERA tar bort något som ligger i databasen, vilket ett
 * pausat besök gör. "Kasta" täckte samma sak som radera och utgick.
 *
 * Ingen fylld knapp. Destruktiva handlingar bär aldrig fyllning i Målindex – rött
 * märker att något inte går att ångra, det ska inte dra ögat dit. Se modalStil.
 */

export interface RaderaBesokModalProps {
  open: boolean
  /** Besökets datum, färdigformaterat. */
  datum: string
  /**
   * Vilket slags besök som raderas. Ett utkast är ett halvt samtal som aldrig blev
   * något; ett sparat besök har hunnit påverka mätningen, och det är den skillnaden
   * texten måste bära.
   */
  sort?: 'utkast' | 'sparat'
  /**
   * Uppföljningar vars Målindex viktades av det här besöket och därför försvinner.
   * Räknas fram av anroparen – modalen ska inte känna till hur viktningen fungerar.
   */
  paverkade?: { id: number; datum: string; index: string }[]
  onBekrafta: () => void
  onAvbryt: () => void
}

const RaderaBesokModal: React.FC<RaderaBesokModalProps> = ({
  open,
  datum,
  sort = 'utkast',
  paverkade = [],
  onBekrafta,
  onAvbryt,
}) => (
  <Modal
    open={open}
    {...MODAL_GRIND}
    title={modalTitel('Radera besöket?')}
    width={MODAL_BREDD}
    onCancel={onAvbryt}
    footer={
      <div className="flex flex-wrap justify-end gap-2">
        <Button {...MODAL_KNAPP} onClick={onAvbryt}>
          Tillbaka
        </Button>
        <Button {...MODAL_KNAPP_FARLIG} onClick={onBekrafta}>
          Radera besöket
        </Button>
      </div>
    }
  >
    {sort === 'utkast' ? (
      <>
        <p className="m-0 text-base text-gray-700">
          Det påbörjade besöket från <span className="font-medium">{datum}</span> raderas. Svaren ni hann fylla i
          försvinner och går inte att få tillbaka.
        </p>
        <p className="mt-3 mb-0 text-base text-gray-700">Tidigare besök, mål och index rörs inte.</p>
      </>
    ) : (
      <>
        <p className="m-0 text-base text-gray-700">
          Besöket från <span className="font-medium">{datum}</span> raderas. Skattningarna, målen och anteckningen
          försvinner ur uppföljningen och går inte att få tillbaka.
        </p>

        {/* Samma amberruta som när en ny kartläggning inaktiverar mål: den som trycker
            ska se exakt vad som går förlorat, inte en formulering om att något kan gå
            förlorat. Talen står med – det är dem man förlorar. */}
        {paverkade.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
            <p className="m-0 text-base text-gray-700">Målindex som tas bort:</p>
            <div className="mt-2 flex flex-col gap-1.5">
              {paverkade.map((p) => (
                <p key={p.id} className="m-0 text-base text-gray-800">
                  Uppföljningen {p.datum} <span className="font-medium">{p.index}</span>
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Skälet att knappen finns: ett felaktigt besök ligger inte bara och skräpar,
            det viktar nästa mätning. */}
        <p className="mt-3 mb-0 text-base text-gray-700">
          Besöket viktar inte längre kommande mätningar.
          {paverkade.length === 0 && ' Övriga besök rörs inte.'}
        </p>
      </>
    )}
  </Modal>
)

export default RaderaBesokModal
